import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PublicNav } from "@/components/PublicNav";
import { Footer } from "@/components/Footer";
import { PrototypeBanner } from "@/components/PrototypeBanner";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ShieldCheck, MapPin, Euro, HeartHandshake, Stethoscope, Activity, HeartPulse, Brain,
  MessageSquare, UserCheck, Plus, CheckCircle2, ArrowRight, Clock, UserRound, Lock, MessageCircle, Video,
} from "lucide-react";

const ICONS = { Activity, HeartPulse, MessageSquare, Brain, Stethoscope, UserCheck, Plus };

const HERO_IMG = "https://images.unsplash.com/photo-1758691462321-9b6c98c40f7e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxkb2N0b3IlMjBob21lJTIwdmlzaXQlMjBlbGRlcmx5JTIwcGF0aWVudCUyMGNhcmluZyUyMG51cnNlfGVufDB8fHx8MTc5MDc3NTA3M3ww&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  { icon: ShieldCheck, title: "Professionisti verificati", text: "Iscritti agli albi sanitari e controllati dal nostro team." },
  { icon: MapPin, title: "Arezzo e provincia", text: "Copertura rapida a domicilio nei comuni del territorio." },
  { icon: Euro, title: "Tariffe chiare", text: "Prezzi trasparenti e pagamento sicuro, senza sorprese." },
  { icon: HeartHandshake, title: "Semplice per tutti", text: "Pensato per anziani e familiari che prenotano a distanza." },
];

const STEPS = [
  { n: "1", t: "Scegli il servizio", d: "Seleziona tra visite mediche, infermiere, fisioterapia, logopedia e assistenza." },
  { n: "2", t: "Indica data e zona", d: "Inserisci comune e indirizzo ad Arezzo o provincia e l'orario preferito." },
  { n: "3", t: "Scegli il professionista", d: "Confronta profili, recensioni e tariffe dei sanitari disponibili." },
  { n: "4", t: "Conferma e paga", d: "Riepilogo chiaro e pagamento protetto in modalità demo o test Stripe." },
];

const FAQS = [
  { q: "Cos'è EtruriaCare?", a: "Una piattaforma che collega famiglie e professionisti sanitari qualificati per prestazioni a domicilio ad Arezzo e provincia. Siamo un intermediario tecnologico: la prestazione resta responsabilità del professionista." },
  { q: "Quali servizi posso richiedere?", a: "Fisioterapia, assistenza infermieristica, logopedia, psicologia, visite mediche e assistenza domiciliare, direttamente a casa." },
  { q: "I professionisti sono verificati?", a: "Sì. Ogni professionista viene verificato dal nostro team (iscrizione all'albo, partita IVA, polizza RC) prima di essere visibile ai pazienti." },
  { q: "Come funziona il pagamento?", a: "In questo prototipo il pagamento è in modalità demo (nessun addebito reale) oppure tramite Stripe in ambiente test. Non memorizziamo dati delle carte." },
  { q: "Vengono raccolti dati clinici?", a: "No. EtruriaCare raccoglie solo dati organizzativi necessari alla prenotazione. Non inserire referti, diagnosi o terapie nei campi descrittivi." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["services-all"], queryFn: async () => (await api.get("/services?only_active=false")).data });
  const services = (data?.services || []).filter((s) => s.slug !== "altro").slice(0, 6);
  const waLink = `https://wa.me/393882454770?text=${encodeURIComponent("Ciao EtruriaCare, vorrei informazioni per prenotare una prestazione a domicilio ad Arezzo. (Non invio dati sanitari o clinici in questa chat.)")}`;
  const trackWa = () => { api.post("/track/whatsapp-click", { source: "landing" }).catch(() => {}); };

  return (
    <div className="min-h-screen bg-white">
      <PrototypeBanner />
      <PublicNav />

      {/* HERO */}
      <section className="bg-[#EAF3EF] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="ec-fade-in">
            <span className="inline-flex items-center gap-2 bg-white text-[#087A73] text-sm font-semibold px-4 py-2 rounded-full border border-[#D2E3DB] mb-6">
              <MapPin className="h-4 w-4" /> Servizio attivo ad Arezzo e provincia
            </span>
            <h1 className="font-serif-accent text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#102A2A] leading-tight">
              Il tuo medico a domicilio in giornata.
            </h1>
            <p className="mt-4 text-xl sm:text-2xl font-semibold text-[#087A73]">Assistenza medica professionale a casa tua, ad Arezzo e provincia.</p>
            <p className="mt-5 text-lg text-[#5C6B68] leading-relaxed max-w-xl">
              EtruriaCare collega famiglie e professionisti sanitari qualificati e verificati per prestazioni direttamente al tuo domicilio.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button data-testid="hero-richiedi-btn" onClick={() => navigate("/login?role=patient")}
                className="bg-[#087A73] hover:bg-[#06615B] text-white rounded-xl h-14 px-8 text-base font-semibold shadow-sm">
                Richiedi una visita <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button data-testid="hero-pro-btn" variant="outline" onClick={() => navigate("/login?role=professional")}
                className="rounded-xl h-14 px-8 text-base font-semibold border-2 border-[#087A73] text-[#087A73] hover:bg-[#087A73]/10">
                Sono un professionista
              </Button>
            </div>
            <a data-testid="hero-whatsapp-contact" href={waLink} target="_blank" rel="noopener noreferrer" onClick={trackWa}
              className="mt-5 inline-flex items-center gap-2 text-[#5C6B68] hover:text-[#087A73] transition-colors">
              <span className="h-9 w-9 rounded-xl bg-[#25D366] text-white flex items-center justify-center shrink-0"><MessageCircle className="h-5 w-5" /></span>
              <span className="text-sm sm:text-base">Hai dubbi o domande? <strong className="text-[#087A73]">Contattaci su WhatsApp al 388 2454770</strong></span>
            </a>
          </div>
          <div className="relative">
            <img src={HERO_IMG} alt="Professionista sanitario in visita domiciliare a un paziente anziano"
              className="rounded-3xl shadow-lg w-full h-[300px] sm:h-[420px] object-cover" />
            <div className="absolute -bottom-5 left-5 right-5 sm:left-8 sm:right-auto bg-white rounded-2xl shadow-md border border-[#D2E3DB] p-4 flex items-center gap-3">
              <span className="h-11 w-11 rounded-xl bg-[#E0EFEA] text-[#087A73] flex items-center justify-center"><ShieldCheck /></span>
              <div>
                <p className="text-sm font-bold text-[#102A2A]">100% professionisti verificati</p>
                <p className="text-xs text-[#5C6B68]">Iscritti agli albi sanitari</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TELECONSULTO BANNER */}
      <section data-testid="home-teleconsulto-banner" className="bg-[#087A73]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col md:flex-row items-center gap-6">
          <div className="h-14 w-14 rounded-2xl bg-white/15 text-white flex items-center justify-center shrink-0"><Video className="h-7 w-7" /></div>
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-xl sm:text-2xl font-bold text-white">Ricette e certificati senza uscire di casa</h2>
            <p className="text-[#EAF3EF] mt-1 text-sm sm:text-base max-w-2xl">
              Con il <strong>Teleconsulto</strong> parli con un medico in videochiamata e ottieni ricette, prescrizioni e certificati. Per iniziare, tocca il pulsante qui a fianco e scegli <strong>“Teleconsulto”</strong> come servizio.
            </p>
          </div>
          <Button data-testid="home-teleconsulto-cta" onClick={() => navigate("/login?role=patient")}
            className="bg-white text-[#087A73] hover:bg-[#EAF3EF] rounded-xl h-12 px-6 font-semibold shrink-0 shadow-sm">
            Richiedi un teleconsulto <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      <section id="perche" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">Perché EtruriaCare</h2>
        <p className="text-center text-[#5C6B68] mb-10 max-w-2xl mx-auto">Cura vicina, umana e affidabile, pensata per il territorio aretino.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="bg-white rounded-2xl border border-[#D2E3DB] shadow-sm p-6 hover:shadow-md transition-all">
                <span className="h-12 w-12 rounded-xl bg-[#E0EFEA] text-[#087A73] flex items-center justify-center mb-4"><Icon /></span>
                <h3 className="text-lg font-bold text-[#102A2A] mb-1">{p.title}</h3>
                <p className="text-sm text-[#5C6B68] leading-relaxed">{p.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#EAF3EF] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold">I nostri servizi</h2>
              <p className="text-[#5C6B68] mt-2">Prestazioni sanitarie eseguite al tuo domicilio da personale abilitato.</p>
            </div>
            <Button variant="outline" onClick={() => navigate("/servizi")}
              className="rounded-xl h-12 border-2 border-[#087A73] text-[#087A73] hover:bg-[#087A73]/10 font-semibold">
              Tutti i servizi
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => {
              const Icon = ICONS[s.icon] || Stethoscope;
              const soon = !s.active;
              return (
                <div key={s.id} data-testid={`landing-service-${s.slug}`}
                  className={`relative bg-white rounded-2xl border border-[#D2E3DB] shadow-sm p-6 transition-all ${soon ? "" : "hover:shadow-md hover:-translate-y-0.5"}`}>
                  {soon && <span data-testid={`landing-soon-${s.slug}`} className="absolute top-4 right-4 bg-[#FBF0D6] text-[#8a6400] text-xs font-bold px-3 py-1 rounded-full border border-[#D99A00]/40">Prossimamente</span>}
                  <span className={`h-12 w-12 rounded-xl flex items-center justify-center mb-4 ${soon ? "bg-[#EAF3EF] text-[#087A73]" : "bg-[#087A73] text-white"}`}><Icon /></span>
                  <h3 className="text-lg font-bold text-[#102A2A] mb-2">{s.name}</h3>
                  <p className="text-sm text-[#5C6B68] leading-relaxed mb-4">{s.description}</p>
                  <div className="flex items-center justify-between">
                    {soon ? (
                      <span className="text-sm font-semibold text-[#8a6400]">Disponibile prossimamente</span>
                    ) : (
                      <>
                        <span className="text-[#087A73] font-bold">Tariffa su richiesta</span>
                        <span className="text-xs text-[#5C6B68] flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-[#087A73]" /> A domicilio</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="come-funziona" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">Come funziona</h2>
        <p className="text-center text-[#5C6B68] mb-12 max-w-2xl mx-auto">In quattro semplici passi hai un professionista a casa.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className="relative bg-white rounded-2xl border border-[#D2E3DB] shadow-sm p-6">
              <span className="h-11 w-11 rounded-full bg-[#087A73] text-white font-bold text-lg flex items-center justify-center mb-4">{s.n}</span>
              <h3 className="font-bold text-[#102A2A] mb-1">{s.t}</h3>
              <p className="text-sm text-[#5C6B68] leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECURITY */}
      <section id="sicurezza" className="bg-[#102A2A] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-[#087A73] bg-white/10 px-4 py-2 rounded-full text-sm font-semibold mb-5"><Lock className="h-4 w-4" /> Sicurezza e privacy</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">La tua tranquillità, prima di tutto</h2>
            <p className="text-[#EAF3EF]/80 leading-relaxed mb-6">
              Adottiamo un approccio privacy by design. Raccogliamo solo dati organizzativi, mai dati clinici. Le sessioni sono protette e i pagamenti non memorizzano dati delle carte.
            </p>
            <ul className="space-y-3">
              {["Nessun dato clinico raccolto", "Professionisti verificati e iscritti agli albi", "Consenso privacy separato e tracciato", "Pagamenti sicuri, nessun dato di carta salvato"].map((t) => (
                <li key={t} className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-[#087A73] shrink-0 mt-0.5" /><span className="text-[#EAF3EF]/90">{t}</span></li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[{ i: ShieldCheck, t: "Verifica albi" }, { i: Lock, t: "Dati protetti" }, { i: UserRound, t: "Solo dati organizzativi" }, { i: Clock, t: "Disponibilità reale" }].map((b) => {
              const Icon = b.i;
              return (
                <div key={b.t} className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                  <Icon className="h-8 w-8 text-[#087A73] mx-auto mb-3" />
                  <p className="text-sm font-semibold text-white">{b.t}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10">Domande frequenti</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`item-${i}`} data-testid={`faq-${i}`}
              className="bg-white rounded-2xl border border-[#D2E3DB] px-5 shadow-sm">
              <AccordionTrigger className="text-left font-semibold text-[#102A2A] hover:no-underline py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[#5C6B68] leading-relaxed pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="bg-[#087A73] py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Hai bisogno di assistenza a domicilio?</h2>
          <p className="text-[#EAF3EF] mb-8 text-lg">Prenota in pochi minuti un professionista verificato ad Arezzo e provincia.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="cta-richiedi-btn" onClick={() => navigate("/login?role=patient")}
              className="bg-white text-[#087A73] hover:bg-[#EAF3EF] rounded-xl h-14 px-10 text-base font-bold shadow-sm">
              Richiedi una visita ora
            </Button>
            <a data-testid="cta-whatsapp-btn" href={waLink} target="_blank" rel="noopener noreferrer" onClick={trackWa}
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb457] text-white rounded-xl h-14 px-8 text-base font-bold shadow-sm transition-colors">
              <MessageCircle className="h-5 w-5" /> Avvia su WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
